import { AllocationBucketTypes } from '@/features/Collaborators/Models';
import {
    DAYS_PER_MONTH,
    WEEKS_PER_MONTH,
    WORKING_DAYS_PER_MONTH,
} from '../exportsPage.definitions';
import type { AllocationHoursItem } from '../exportsPage.types';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** Dias inteiros entre duas datas, ignorando horário. */
function daysBetween(start: Date, end: Date): number {
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((endDay.getTime() - startDay.getTime()) / MILLISECONDS_PER_DAY);
}

/**
 * Converte o bucket da alocação em horas por mês.
 *
 * Espelha `calculateMonthlyHours` da cloud function `matchCollaborators`, para
 * que a planilha e o cálculo de disponibilidade do sistema deem o mesmo número:
 * diário x21 dias úteis, semanal x4 semanas, mensal direto e pré-definido
 * rateado pela duração da alocação (em meses de 30 dias).
 *
 * Alocação sem quantidade de horas conta zero — não há como inferir o consumo.
 */
export function calculateAllocationMonthlyHours(
    allocation: Pick<AllocationHoursItem, 'bucket' | 'bucketType' | 'startDate' | 'endDate'>
): number {
    const { bucket, bucketType, startDate, endDate } = allocation;

    if (typeof bucket !== 'number' || !Number.isFinite(bucket) || bucket <= 0) {
        return 0;
    }

    switch (bucketType) {
        case AllocationBucketTypes.Daily:
            return round(bucket * WORKING_DAYS_PER_MONTH);
        case AllocationBucketTypes.Weekly:
            return round(bucket * WEEKS_PER_MONTH);
        case AllocationBucketTypes.Monthly:
            return round(bucket);
        case AllocationBucketTypes.Defined: {
            if (!startDate || !endDate) {
                return round(bucket);
            }

            const days = daysBetween(startDate, endDate) + 1;
            if (days <= 0) {
                return round(bucket);
            }

            const months = days / DAYS_PER_MONTH;
            return round(bucket / (months || 1));
        }
        default:
            return round(bucket);
    }
}

function round(value: number): number {
    return Number.parseFloat(value.toFixed(2));
}
